import epochToFormattedDateTime from "utils/formatter-utils/date-formatter";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import CountdownClock from "../pool-item/countdown-clock";
import { isPoolClosed } from "utils/pool/is-pool-closed";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import ContestAddition from "../pool-item/contest-addition";
import { formatAsCurrency } from "utils/formatter-utils/currency-formatter";
import { SetStateAction, useState, useMemo } from "react";
import listItemStyles from "assets/shared-stylesheets/sparket-list-item.module.css";
import { InfoCircle } from "react-bootstrap-icons";
import RulesModal from "components/pool-details/rules-modal";
import ContestantCard from "./contestant-card";
import { Entry } from "interfaces/entry";
import { Card, Dropdown } from "react-bootstrap";
import { useAppSelector } from "state/hooks";
import { getCardColor, getTextColor } from "state/slices/group-slice";
import { Pool } from "interfaces/pool.ts";
import { BetTypeName } from "interfaces/bet-type-name";
import { Contest, isBasicContest, picksCostForContestPick } from "interfaces/leaderboard/contest";
import useContestPickedContestantIds from "hooks/useContestPickedContestantIds";
import { winOddsCellStyle } from "components/homepage-pools-list/win-odds-column-styles";

interface HomepagePoolItemProps {
  pool: Pool;
  showCountDownClock: boolean;
  isContestPool?: boolean;
  setSelectedEntry: SetStateAction<any>;
  isAllowedToBet: () => Promise<boolean>;
  contestEntryId?: string;
  contest?: Contest;
  contestEntryBalance?: number;
  eventIconUrl?: string;
}

const HomepagePoolItem = ({
  pool,
  showCountDownClock,
  isContestPool,
  setSelectedEntry,
  isAllowedToBet,
  contestEntryId,
  contest,
  contestEntryBalance,
  eventIconUrl,
}: HomepagePoolItemProps) => {
  const isBasic = isBasicContest(contest);
  const rawPickedIds = useContestPickedContestantIds(pool.id, pool.entries, contestEntryId);
  const pickedContestantIds = isBasic ? rawPickedIds : new Set<string>();
  const [wasClickedId, setWasClickedId] = useState(-1);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showBetTypeDropdown, setShowBetTypeDropdown] = useState(false);

  const textColor = useAppSelector(getTextColor);
  const cardColor = useAppSelector(getCardColor);

  // Determine default bet type based on priority: win > exacta > trifecta
  const getDefaultBetType = (poolTypes: BetTypeName[]): BetTypeName => {
    if (poolTypes.includes("win")) return "win";
    if (poolTypes.includes("exacta")) return "exacta";
    if (poolTypes.includes("trifecta")) return "trifecta";
    return poolTypes[0];
  };

  const [selectedBetType, setSelectedBetType] = useState<BetTypeName>(
    getDefaultBetType(pool.pool_types || [])
  );

  const handleContestantClick = async (entry: any) => {
    if (await isAllowedToBet()) {
      setWasClickedId(entry.pool_entry_id);
      setSelectedEntry(entry, pool);
    }
  };

  // Filter entries based on selected bet type
  const filteredEntries = useMemo(() => {
    if (!pool.entries) return [];
    if ((pool.pool_types || []).length <= 1) return pool.entries;
    return pool.entries.filter((entry) => entry.pool_type === selectedBetType);
  }, [pool.entries, selectedBetType, pool.pool_types]);

  const totalSeedDistribution = (filteredEntries ?? []).reduce(
    (acc: number, item: Entry) => {
      return acc + item.seed_distribution;
    },
    0
  );

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <>
      <div
        className={listItemStyles.itemContainer}
        style={{
          minHeight: 90,
          marginLeft: 0,
          marginRight: 0,
          height: 250,
          backgroundColor: cardColor,
          color: textColor,
        }}
      >
        <div
          className={listItemStyles.leftColumnContainer}
          style={{ flex: 0.98, paddingTop: 5 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              {eventIconUrl && (
                <img
                  src={eventIconUrl}
                  alt=""
                  style={{
                    width: 45,
                    height: 45,
                    marginRight: 10,
                    borderRadius: 6,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              )}
              <div>
                <BlockyHeavyText
                  style={{
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  {pool.event_name}
                </BlockyHeavyText>
                <BlockyHeavyText className={listItemStyles.poolNameText}>
                  {pool.name}
                </BlockyHeavyText>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {(pool.pool_types || []).length > 1 && (
                <Dropdown
                  show={showBetTypeDropdown}
                  onToggle={(isOpen) => setShowBetTypeDropdown(isOpen)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    style={{
                      fontSize: 11,
                      backgroundColor: cardColor,
                      color: textColor,
                      borderColor: textColor,
                      padding: "4px 10px",
                    }}
                  >
                    {capitalizeFirstLetter(selectedBetType)}
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    style={{
                      backgroundColor: cardColor,
                      minWidth: "auto",
                    }}
                  >
                    {(pool.pool_types || []).map((betType) => {
                      const cost = picksCostForContestPick(contest, betType);
                      const freshCount =
                        (pool.contestants?.length ?? 0) - pickedContestantIds.size;
                      const unaffordable =
                        isBasic && (contestEntryBalance ?? 0) < cost;
                      const notEnoughFresh = isBasic && freshCount < cost;
                      const unselectable = unaffordable || notEnoughFresh;
                      const tooltip = unaffordable
                        ? `Uses ${cost} picks — you have ${contestEntryBalance ?? 0}`
                        : notEnoughFresh
                        ? `Needs ${cost} different contestants — only ${freshCount} left`
                        : undefined;
                      return (
                        <Dropdown.Item
                          key={betType}
                          as="button"
                          disabled={unselectable}
                          title={tooltip}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (unselectable) return;
                            setSelectedBetType(betType);
                            setShowBetTypeDropdown(false);
                          }}
                          active={betType === selectedBetType}
                          style={{
                            fontSize: 11,
                            backgroundColor:
                              betType === selectedBetType ? textColor : cardColor,
                            color:
                              betType === selectedBetType ? cardColor : textColor,
                            opacity: unselectable ? 0.5 : undefined,
                            cursor: unselectable ? "not-allowed" : "pointer",
                          }}
                        >
                          {capitalizeFirstLetter(betType)}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <InfoCircle
                onClick={(e) => {
                  e.preventDefault();
                  setShowRulesModal(true);
                }}
              />
            </div>
          </div>
          <div
            style={{
              height: 140,
              overflow: "scroll",
              overflowX: "hidden",
              scrollbarColor: "gray " + cardColor,
            }}
          >
            <Card
              style={{
                position: "sticky",
                top: -1,
                backgroundColor: cardColor,
                color: textColor,
                fontSize: 11,
                zIndex: 100,
                borderRadius: 0,
                border: "none",
                width: "101%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div></div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12,
                    width: 95,
                    paddingRight: 10,
                  }}
                >
                  <BlockyBoldText style={winOddsCellStyle}>
                    Win %
                  </BlockyBoldText>
                  <BlockyBoldText style={winOddsCellStyle}>
                    Odds
                  </BlockyBoldText>
                </div>
              </div>
            </Card>
            {filteredEntries.map((entry: Entry) => {
              const wasClicked =
                wasClickedId.toString() === entry.pool_entry_id;
              const idsForEntry = (entry.contestant_id || "")
                .split(";")
                .filter((id) => id);
              const alreadyPicked = idsForEntry.some((id) =>
                pickedContestantIds.has(id)
              );
              return (
                <ContestantCard
                  key={entry.pool_entry_id}
                  entry={entry}
                  handleContestantClick={handleContestantClick}
                  totalSeedDistribution={totalSeedDistribution}
                  wasClicked={wasClicked}
                  poolStatus={pool.status}
                  contestEntryId={contestEntryId}
                  winnerPoolEntryIds={pool.winner_pool_entry_ids}
                  alreadyPicked={alreadyPicked}
                />
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              paddingRight: 15,
            }}
          >
            <div id="homepage-pool-item-pool-size">
              <div>
                <BlockyHeavyText className={listItemStyles.poolCloseDatePrefix}>
                  Pool Size:&nbsp;
                </BlockyHeavyText>
              </div>
              <BlockyMediumText style={{ fontSize: 12, display: "inline" }}>
                {formatAsCurrency(pool["total"])}
              </BlockyMediumText>
            </div>
            <div id="homepage-pool-item-close-date">
              <div>
                <BlockyBoldText className={listItemStyles.poolCloseDatePrefix}>
                  {isPoolClosed(pool.status) ? "Closed" : "Closes"}:
                </BlockyBoldText>
              </div>
              <BlockyMediumText style={{ fontSize: 12, display: "inline" }}>
                {epochToFormattedDateTime(pool.close)}
              </BlockyMediumText>
              <BlockyMediumText className={listItemStyles.closeDateText}>
                {showCountDownClock && (
                  <CountdownClock close={new Date(pool.close)} />
                )}
              </BlockyMediumText>
              {isContestPool && <ContestAddition status={pool.status} />}
            </div>
          </div>
        </div>
      </div>

      <div onClick={(e) => e.preventDefault()}>
        <RulesModal
          pool={pool}
          showRulesModal={showRulesModal}
          setShowRulesModal={setShowRulesModal}
        ></RulesModal>
      </div>
    </>
  );
};

export default HomepagePoolItem;
